// Starting the "Continue with Instagram" round trip, the same way the Stripe
// one works: the in-app browser sheet on the phone (so the app stays
// underneath and the return lands back inside it), a plain redirect on the
// web. The server decides everything else — see lib/identity/instagram.js.
import { Browser } from "@capacitor/browser";
import { instagramApi } from "../services/instagram";
import type { SignupIdentity } from "../types/auth";
import { IS_NATIVE } from "./stripeFlow";

export type IgVerifyStatus = "done" | "cancelled" | "expired" | "taken" | "personal" | "failed";

/** Where the talent should land when Instagram sends them back. */
export type IgReturnTo = "qr" | "profile";

// Instagram's sign-in page: the in-app sheet on the phone, the whole page on the web.
async function openSignIn(url: string): Promise<boolean> {
  if (IS_NATIVE) {
    try {
      await Browser.open({ url });
      return true;
    } catch {
      // The sheet refused to present (should never happen): fall through to a
      // plain redirect rather than leaving the talent on a dead button.
    }
  }
  window.location.href = url;
  return true;
}

/**
 * Opens Instagram's sign-in. Resolves false when we never got a URL (the
 * feature is off, or the network failed) so the caller can show one line
 * instead of a dead button.
 */
export async function startInstagramLogin(to: IgReturnTo): Promise<boolean> {
  let url: string | undefined;
  try {
    const res = await instagramApi.oauthUrl({ ...(IS_NATIVE ? { client: "app" as const } : {}), to });
    url = res?.url;
  } catch {
    return false;
  }
  if (!url) return false;
  return openSignIn(url);
}

/**
 * Talent sign-up (build 54): Instagram's sign-in before the account exists. The
 * server ties it to a claim (the token `onClaim` receives — keep it, register
 * sends it). `beforeLeaving` saves the sign-up so far first (name, photo, the
 * claim): the website leaves the page for Instagram, and in the app the way
 * back (a deep link) mounts sign-up afresh from what was saved.
 * Resolves false when it couldn't start.
 */
export async function startInstagramSignup({
  identity,
  claim,
  onClaim,
  beforeLeaving,
}: {
  identity: SignupIdentity;
  claim?: string;
  onClaim: (claim: string) => void;
  beforeLeaving: (claim: string) => Promise<void>;
}): Promise<boolean> {
  let res;
  try {
    res = await instagramApi.signupOauthUrl({ ...identity, client: IS_NATIVE ? "app" : "web", ...(claim ? { claim } : {}) });
  } catch {
    return false;
  }
  if (!res?.url || !res.claim) return false;
  onClaim(res.claim);
  await beforeLeaving(res.claim);
  return openSignIn(res.url);
}

/** Subscribe to "the Instagram sheet was closed" — callers re-read the profile. */
export function onInstagramBrowserClosed(cb: () => void): () => void {
  if (!IS_NATIVE) return () => {};
  const sub = Browser.addListener("browserFinished", cb);
  return () => {
    sub.then((s) => s.remove()).catch(() => {});
  };
}
